import React, { useState, useEffect } from 'react';
import { AIPrompt, PromptType } from '@/types/ai-prompts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  fetchPromptsByType, 
  createPrompt, 
  updatePrompt, 
  deletePrompt 
} from '@/services/ai-prompt-service';

const promptTypes: PromptType[] = [
  'topic_prompt',
  'language_instruction',
  'suggestion_template',
  'system_instruction',
  'default_suggestion'
];

export const PromptsManagement = () => {
  const [prompts, setPrompts] = useState<AIPrompt[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeType, setActiveType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [currentPrompt, setCurrentPrompt] = useState<AIPrompt | null>(null);
  const [newPrompt, setNewPrompt] = useState<Partial<AIPrompt>>({
    type: 'topic_prompt',
    name: '',
    content: '',
    metadata: { description: '', version: 1, is_active: true }
  });

  // Fetch prompts from Supabase using our service
  const fetchPrompts = async () => {
    setIsLoading(true);
    try {
      let fetchedPrompts: AIPrompt[] = [];
      
      if (activeType === 'all') {
        // Fetch all prompt types
        const promises = promptTypes.map(type => fetchPromptsByType(type));
        const results = await Promise.all(promises);
        fetchedPrompts = results.flat();
      } else {
        fetchedPrompts = await fetchPromptsByType(activeType as PromptType);
      }
      
      if (searchQuery) {
        fetchedPrompts = fetchedPrompts.filter(
          prompt => prompt.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      setPrompts(fetchedPrompts);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      toast.error('Failed to fetch prompts');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchPrompts();
  }, [activeType, searchQuery]);

  // Parse content when editing/creating templates with variables
  const formatTemplateDisplay = (content: string, type: PromptType): string => {
    if (type === 'default_suggestion') {
      try {
        // Handle bullet points or line breaks for suggestions
        const suggestions = parseJsonSuggestions(content);
        return suggestions.join('\n');
      } catch (e) {
        console.error('Error formatting suggestions:', e);
        return content;
      }
    } else if (type === 'suggestion_template') {
      // Highlight template variables for better visibility
      return content.replace(/\$\{([^}]+)\}/g, '${$1}');
    }
    return content;
  };

  // Parse content when editing/creating default suggestions
  const parseDefaultSuggestionContent = (content: string): string => {
    if (currentPrompt?.type === 'default_suggestion' || newPrompt.type === 'default_suggestion') {
      try {
        // Handle bullet points or line breaks
        const lines = content
          .split(/[•\n]/)
          .map(line => line.trim())
          .filter(line => line.length > 0);
        
        // Return JSON string of array
        return JSON.stringify(lines);
      } catch (e) {
        console.error('Error formatting suggestions:', e);
        return '[]';
      }
    }
    return content;
  };

  // Safely parse JSON suggestions
  const parseJsonSuggestions = (content: string): string[] => {
    try {
      const suggestions = JSON.parse(content);
      if (Array.isArray(suggestions)) {
        return suggestions;
      }
      return [];
    } catch (e) {
      console.error('Error parsing JSON suggestions:', e);
      return [];
    }
  };

  // Handle editing a prompt
  const handleEditClick = (prompt: AIPrompt) => {
    setCurrentPrompt(prompt);
    setIsEditing(true);
  };

  // Handle creating a new prompt
  const handleCreateClick = () => {
    setNewPrompt({
      type: 'topic_prompt',
      name: '',
      content: '',
      metadata: { description: '', version: 1, is_active: true }
    });
    setIsCreating(true);
  };

  // Handle deleting a prompt
  const handleDeleteClick = (prompt: AIPrompt) => {
    setCurrentPrompt(prompt);
    setIsDeleting(true);
  };

  // Save edited prompt with special handling for different types
  const saveEditedPrompt = async () => {
    if (!currentPrompt) return;
    
    try {
      // Special handling for default_suggestion type
      let contentToSave = currentPrompt.content;
      if (currentPrompt.type === 'default_suggestion') {
        contentToSave = parseDefaultSuggestionContent(contentToSave);
      }
      
      await updatePrompt(currentPrompt.id, {
        content: contentToSave,
        name: currentPrompt.name,
        type: currentPrompt.type,
        metadata: currentPrompt.metadata
      });
      
      toast.success('Prompt updated successfully');
      setIsEditing(false);
      fetchPrompts();
    } catch (error) {
      console.error('Error updating prompt:', error);
      toast.error('Failed to update prompt');
    }
  };

  // Create new prompt with special handling for different types
  const createNewPrompt = async () => {
    try {
      // Special handling for default_suggestion type
      let contentToSave = newPrompt.content || '';
      if (newPrompt.type === 'default_suggestion') {
        contentToSave = parseDefaultSuggestionContent(contentToSave);
      }
      
      await createPrompt({
        type: newPrompt.type as PromptType,
        name: newPrompt.name || '',
        content: contentToSave,
        metadata: newPrompt.metadata || {}
      });
      
      toast.success('Prompt created successfully');
      setIsCreating(false);
      fetchPrompts();
    } catch (error) {
      console.error('Error creating prompt:', error);
      toast.error('Failed to create prompt');
    }
  };

  // Delete prompt
  const deletePromptHandler = async () => {
    if (!currentPrompt) return;
    
    try {
      await deletePrompt(currentPrompt.id);
      
      toast.success('Prompt deleted successfully');
      setIsDeleting(false);
      fetchPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
      toast.error('Failed to delete prompt');
    }
  };

  // Group prompts by type for display
  const groupedPrompts = prompts.reduce<Record<string, AIPrompt[]>>(
    (acc, prompt) => {
      if (!acc[prompt.type]) {
        acc[prompt.type] = [];
      }
      acc[prompt.type].push(prompt);
      return acc;
    },
    {}
  );

  // Helper function to format display name for prompt type
  const getDisplayNameForType = (type: string): string => {
    return type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Helper function to render prompt content based on type
  const renderPromptContent = (prompt: AIPrompt) => {
    switch (prompt.type) {
      case 'default_suggestion':
        return (
          <ul className="text-sm text-muted-foreground pl-5">
            {parseJsonSuggestions(prompt.content).map((suggestion, i) => (
              <li key={i}>{suggestion}</li>
            ))}
          </ul>
        );
      case 'suggestion_template':
        return (
          <div>
            <p className="text-sm text-muted-foreground line-clamp-3 font-mono">
              {prompt.content.substring(0, 150)}...
            </p>
            <div className="mt-2 text-xs text-muted-foreground">
              <span className="font-semibold">Variables: </span>
              {prompt.content.match(/\$\{([^}]+)\}/g)?.map(v => 
                v.replace(/\$\{|\}/g, '')
              ).join(', ') || 'None'}
            </div>
          </div>
        );
      default:
        return (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {prompt.content}
          </p>
        );
    }
  };

  // Helper function to render the content textarea based on prompt type
  const renderContentInput = () => {
    const prompt = currentPrompt || newPrompt;
    const isEditing = !!currentPrompt;
    
    if (prompt.type === 'default_suggestion') {
      const value = isEditing 
        ? parseJsonSuggestions(prompt.content).join('\n')
        : prompt.content || '';
        
      return (
        <div className="col-span-3">
          <p className="text-sm text-muted-foreground mb-2">
            Enter one suggestion per line. Each line will be a separate suggestion button in the AI assistant.
          </p>
          <Textarea
            id={isEditing ? "prompt-content" : "new-prompt-content"}
            value={value}
            onChange={(e) => {
              if (isEditing) {
                setCurrentPrompt({ ...currentPrompt!, content: e.target.value });
              } else {
                setNewPrompt({ ...newPrompt, content: e.target.value });
              }
            }}
            rows={10}
            className="font-mono"
            placeholder="Enter one suggestion per line"
          />
        </div>
      );
    } else if (prompt.type === 'suggestion_template') {
      return (
        <div className="col-span-3">
          <p className="text-sm text-muted-foreground mb-2">
            Enter the template with variables in the format: ${'{variable}'}.
            Available variables: <code>topic</code>, <code>lastUserMessage</code>, <code>content</code>
          </p>
          <Textarea
            id={isEditing ? "prompt-content" : "new-prompt-content"}
            value={prompt.content || ''}
            onChange={(e) => {
              if (isEditing) {
                setCurrentPrompt({ ...currentPrompt!, content: e.target.value });
              } else {
                setNewPrompt({ ...newPrompt, content: e.target.value });
              }
            }}
            rows={12}
            className="font-mono"
            placeholder="Enter template content with variables like ${topic}, ${lastUserMessage}, ${content}"
          />
        </div>
      );
    } else {
      return (
        <Textarea
          id={isEditing ? "prompt-content" : "new-prompt-content"}
          value={prompt.content || ''}
          onChange={(e) => {
            if (isEditing) {
              setCurrentPrompt({ ...currentPrompt!, content: e.target.value });
            } else {
              setNewPrompt({ ...newPrompt, content: e.target.value });
            }
          }}
          rows={10}
          className={isEditing ? "col-span-3 font-mono" : "font-mono"}
        />
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">AI Prompts Management</h2>
        <Button onClick={handleCreateClick}>
          <Plus className="mr-2 h-4 w-4" />
          Create New Prompt
        </Button>
      </div>
      
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search prompts..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      
      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList>
          <TabsTrigger value="all">All Types</TabsTrigger>
          {promptTypes.map((type) => (
            <TabsTrigger key={type} value={type}>
              {getDisplayNameForType(type)}
            </TabsTrigger>
          ))}
        </TabsList>
        
        <div className="mt-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : prompts.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-muted-foreground">No prompts found. Create a new one to get started.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedPrompts).map(([type, typePrompts]) => (
                <div key={type} className="space-y-4">
                  <h3 className="text-lg font-semibold capitalize">
                    {getDisplayNameForType(type)}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {typePrompts.map((prompt) => (
                      <Card key={prompt.id} className="overflow-hidden">
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-base">{prompt.name}</CardTitle>
                              <CardDescription className="text-xs mt-1">
                                {prompt.metadata?.description || 'No description'}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="prose prose-sm dark:prose-invert max-h-32 overflow-hidden text-ellipsis">
                            {renderPromptContent(prompt)}
                          </div>
                          <div className="flex justify-between mt-4 pt-2 border-t">
                            <span className="text-xs text-muted-foreground">
                              Version: {prompt.metadata?.version || 1}
                            </span>
                            <div className="space-x-2">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleEditClick(prompt)}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-7 w-7 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
                                onClick={() => handleDeleteClick(prompt)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={(open) => !open && setIsEditing(false)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Prompt</DialogTitle>
            <DialogDescription>
              Make changes to the prompt. Click save when you're done.
            </DialogDescription>
          </DialogHeader>
          
          {currentPrompt && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="prompt-type" className="text-right">Type</Label>
                <Select
                  value={currentPrompt.type}
                  onValueChange={(value) => 
                    setCurrentPrompt({ ...currentPrompt, type: value as PromptType })
                  }
                >
                  <SelectTrigger id="prompt-type" className="col-span-3">
                    <SelectValue placeholder="Select prompt type" />
                  </SelectTrigger>
                  <SelectContent>
                    {promptTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="prompt-name" className="text-right">Name</Label>
                <Input
                  id="prompt-name"
                  value={currentPrompt.name}
                  onChange={(e) => 
                    setCurrentPrompt({ ...currentPrompt, name: e.target.value })
                  }
                  className="col-span-3"
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="prompt-description" className="text-right">Description</Label>
                <Input
                  id="prompt-description"
                  value={currentPrompt.metadata?.description || ''}
                  onChange={(e) => 
                    setCurrentPrompt({ 
                      ...currentPrompt, 
                      metadata: { 
                        ...currentPrompt.metadata,
                        description: e.target.value 
                      } 
                    })
                  }
                  className="col-span-3"
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="prompt-version" className="text-right">Version</Label>
                <Input
                  id="prompt-version"
                  type="number"
                  value={currentPrompt.metadata?.version || 1}
                  onChange={(e) => 
                    setCurrentPrompt({ 
                      ...currentPrompt, 
                      metadata: { 
                        ...currentPrompt.metadata,
                        version: parseInt(e.target.value) || 1
                      } 
                    })
                  }
                  className="col-span-3"
                />
              </div>
              
              <div className="grid grid-cols-4 gap-4">
                <Label htmlFor="prompt-content" className="text-right pt-2">Content</Label>
                {renderContentInput()}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button onClick={saveEditedPrompt}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={isCreating} onOpenChange={(open) => !open && setIsCreating(false)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Create New Prompt</DialogTitle>
            <DialogDescription>
              Create a new AI prompt. Fill in all the required fields.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="new-prompt-type" className="text-right">Type *</Label>
              <Select
                value={newPrompt.type as string}
                onValueChange={(value) => 
                  setNewPrompt({ ...newPrompt, type: value as PromptType })
                }
              >
                <SelectTrigger id="new-prompt-type" className="col-span-3">
                  <SelectValue placeholder="Select prompt type" />
                </SelectTrigger>
                <SelectContent>
                  {promptTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="new-prompt-name" className="text-right">Name *</Label>
              <Input
                id="new-prompt-name"
                value={newPrompt.name}
                onChange={(e) => 
                  setNewPrompt({ ...newPrompt, name: e.target.value })
                }
                className="col-span-3"
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="new-prompt-description" className="text-right">Description</Label>
              <Input
                id="new-prompt-description"
                value={newPrompt.metadata?.description || ''}
                onChange={(e) => 
                  setNewPrompt({ 
                    ...newPrompt, 
                    metadata: { 
                      ...newPrompt.metadata,
                      description: e.target.value 
                    } 
                  })
                }
                className="col-span-3"
              />
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <Label htmlFor="new-prompt-content" className="text-right pt-2">Content *</Label>
              {renderContentInput()}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreating(false)}>Cancel</Button>
            <Button 
              onClick={createNewPrompt}
              disabled={!newPrompt.name || !newPrompt.content}
            >
              Create Prompt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleting} onOpenChange={(open) => !open && setIsDeleting(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the prompt:
              <br />
              <span className="font-semibold">{currentPrompt?.name}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deletePromptHandler}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
